import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  FormControlLabel,
  Switch,
  Divider,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../shared/contexts/LanguageContext';
import { useCookieConsent } from './CookieConsentContext';
import { ContentUseCase } from '../../corelogic/usecases/ContentUseCase';

const contentUseCase = new ContentUseCase();

export const CookieSettingsPage: React.FC = () => {
  const { language } = useLanguage();
  const { cookiePreferences, setCookieConsent } = useCookieConsent();
  const navigate = useNavigate();
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false);
  const [languageEnabled, setLanguageEnabled] = useState(false);

  // Initialize state from current preferences
  useEffect(() => {
    setAnalyticsEnabled(cookiePreferences.analytics);
    setLanguageEnabled(cookiePreferences.preferences);
  }, [cookiePreferences]);

  const handleSaveSettings = () => {
    // Check if user selected anything
    if (analyticsEnabled || languageEnabled) {
      // User selected at least one option - accept with selected preferences
      setCookieConsent('accepted', {
        preferences: languageEnabled,
        analytics: analyticsEnabled
      });
    } else {
      // User declined everything - store declined state
      setCookieConsent('declined');
    }

    // Redirect to home page after saving
    navigate('/');
  };

  const content = contentUseCase.getCookieBanner(language);

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        {language === 'de' ? 'Cookie-Einstellungen' : 'Cookie Settings'}
      </Typography>

      <Typography variant="body1" paragraph>
        {language === 'de'
          ? 'Hier können Sie Ihre Cookie-Einstellungen jederzeit ändern. Ihre Änderungen werden sofort gespeichert und angewendet.'
          : 'Here you can change your cookie settings at any time. Your changes will be saved and applied immediately.'
        }
      </Typography>

      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          {language === 'de' ? 'Cookie-Einstellungen anpassen' : 'Adjust Cookie Settings'}
        </Typography>

        <Typography variant="body2" color="text.secondary" paragraph>
          {language === 'de'
            ? 'Aktivieren Sie die Optionen, die Sie zulassen möchten:'
            : 'Enable the options you want to allow:'
          }
        </Typography>

        <Box sx={{ mb: 3 }}>
          <FormControlLabel
            control={
              <Switch
                checked={languageEnabled}
                onChange={(e) => setLanguageEnabled(e.target.checked)}
              />
            }
            label={language === 'de' ? 'Spracheinstellung' : 'Language Preference'}
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4 }}>
            {language === 'de'
              ? 'Diese Einstellung speichert Ihre bevorzugte Sprache für zukünftige Besuche.'
              : 'This setting stores your preferred language for future visits.'
            }
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ mb: 3 }}>
          <FormControlLabel
            control={
              <Switch
                checked={analyticsEnabled}
                onChange={(e) => setAnalyticsEnabled(e.target.checked)}
              />
            }
            label={language === 'de' ? 'Analyse-Cookies (Google Analytics)' : 'Analytics Cookies (Google Analytics)'}
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4 }}>
            {language === 'de'
              ? 'Diese Cookies helfen uns zu verstehen, wie Besucher unsere Website nutzen und die Website zu verbessern.'
              : 'These cookies help us understand how visitors use our website and improve the website.'
            }
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/')}
          >
            {language === 'de' ? 'Abbrechen' : 'Cancel'}
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveSettings}
          >
            {language === 'de' ? 'Speichern & Zurück' : 'Save & Return'}
          </Button>
        </Box>
      </Paper>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
        {language === 'de'
          ? 'Für detaillierte Informationen über Cookies und Datenverarbeitung siehe unsere '
          : 'For detailed information about cookies and data processing, see our '
        }
        <Button
          component="a"
          href="/cookies"
          color="primary"
          sx={{ textTransform: 'none', p: 0, minWidth: 'auto' }}
        >
          {content.cookiePolicy}
        </Button>
        {language === 'de' ? ' und ' : ' and '}
        <Button
          component="a"
          href="/privacy"
          color="primary"
          sx={{ textTransform: 'none', p: 0, minWidth: 'auto' }}
        >
          {content.privacyPolicy}
        </Button>.
      </Typography>
    </Container>
  );
};
